export default function componentStyleOverrides(theme) {
  const surface = theme.colors?.surface;
  const raised = theme.colors?.raised;
  const hover = theme.colors?.hover;
  const inset = theme.colors?.inset;
  const accent = theme.colors?.secondaryMain;
  const accentText = theme.colors?.accentText;
  const accentSoft = theme.colors?.accentSoft;
  const textPrimary = theme.colors?.textPrimary;
  const textSecondary = theme.colors?.textSecondary;
  const textDisabled = theme.colors?.textDisabled;
  const divider = theme.divider;
  const radius = `${theme?.customization?.borderRadius}px`;

  // One visible focus treatment for the whole panel. There was none at all before, so a
  // keyboard user had no way to tell where they were — which matters most on exactly the
  // dense data-entry screens the coaches live in. Spread onto every interactive
  // component below rather than left to each one to remember.
  const focusRing = {
    '&:focus-visible': {
      outline: `2px solid ${accentText}`,
      outlineOffset: '2px'
    }
  };

  // Depth ladder. Previously every MuiPaper — cards, menus, dialogs, popovers alike —
  // got the same `rgba(0,0,0,.24) 0 3px 8px`, so elevation carried no meaning. Cards sit
  // flat on the surface and are separated by a hairline; only things that genuinely
  // float above the page get a shadow, and dialogs get more than menus.
  const shadowMenu = '0 4px 16px rgba(0, 0, 0, 0.35)';
  const shadowDialog = '0 16px 48px rgba(0, 0, 0, 0.5)';

  return {
    MuiCssBaseline: {
      styleOverrides: {
        // The surfaces we didn't draw still carry the design. Scrollbars, selection and
        // the caret shipped as browser defaults that belonged to no design system and
        // rendered as bright light-mode chrome against a dark panel.
        body: {
          scrollbarColor: `${hover} transparent`,
          '&::selection': { backgroundColor: accentSoft },
          '& ::selection': { backgroundColor: accentSoft }
        },
        '*::-webkit-scrollbar': { width: '10px', height: '10px' },
        '*::-webkit-scrollbar-track': { background: 'transparent' },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: hover,
          borderRadius: '8px',
          border: '2px solid transparent',
          backgroundClip: 'content-box'
        },
        '*::-webkit-scrollbar-thumb:hover': { backgroundColor: accent },
        input: { caretColor: accentText },
        // Chrome paints autofilled fields with its OWN background and
        // -webkit-text-fill-color, which beats any `color` we set and renders as muted
        // grey on a dark input — the saved email on the sign-in screen was barely
        // readable. You cannot override the fill with `background`, so the standard
        // approach is an inset box-shadow the size of the field, plus an absurd
        // transition delay so Chrome never repaints its own colour over it.
        'input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, input:-webkit-autofill:active':
          {
            WebkitTextFillColor: textPrimary,
            WebkitBoxShadow: `0 0 0 1000px ${inset} inset`,
            boxShadow: `0 0 0 1000px ${inset} inset`,
            caretColor: accentText,
            borderRadius: 'inherit',
            transition: 'background-color 600000s 0s, color 600000s 0s'
          }
      }
    },
    MuiButton: {
      styleOverrides: {
        contained: {
          color: '#fff',
          backgroundColor: accent,
          '&:hover': {
            backgroundColor: theme.colors?.secondaryDark
          },
          '&.Mui-disabled': {
            backgroundColor: hover,
            color: textDisabled
          }
        },
        outlined: {
          color: textPrimary,
          borderColor: divider,
          '&:hover': {
            borderColor: 'transparent',
            backgroundColor: hover
          }
        },
        text: {
          color: textPrimary,
          '&:hover': { backgroundColor: hover }
        },
        root: {
          // `height: '1.9rem'` overrode MUI's size prop, so dense and large variants were
          // impossible and every target sat at 30px. `color: '#000'` made text and
          // outlined buttons near-invisible on the dark canvas — the single most visible
          // defect in the panel.
          minHeight: '34px',
          fontWeight: 500,
          borderRadius: '8px',
          boxShadow: 'none',
          textTransform: 'capitalize',
          transition: 'background-color .15s ease, border-color .15s ease',
          '&.MuiButton-sizeSmall': { minHeight: '30px' },
          '&.MuiButton-sizeLarge': { minHeight: '42px' },
          ...focusRing
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: textPrimary,
          '&:hover': { backgroundColor: hover },
          '&.Mui-disabled': { color: textDisabled },
          ...focusRing
        }
      }
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: surface,
          boxShadow: 'none'
        },
        rounded: {
          borderRadius: radius
        },
        // Anything that genuinely floats gets depth; a flat card no longer does.
        elevation1: { boxShadow: shadowMenu },
        elevation8: { boxShadow: shadowMenu },
        elevation24: { boxShadow: shadowDialog }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: raised,
          backgroundImage: 'none',
          boxShadow: shadowDialog,
          border: `1px solid ${divider}`
        }
      }
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.0625rem',
          fontWeight: 600,
          color: textPrimary,
          padding: '20px 24px 12px'
        }
      }
    },
    MuiDialogContent: {
      styleOverrides: {
        root: { padding: '8px 24px 20px' }
      }
    },
    MuiDialogActions: {
      styleOverrides: {
        root: { padding: '12px 24px 20px', gap: '8px' }
      }
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          color: textPrimary,
          padding: '16px 20px'
        },
        title: {
          fontSize: '1.0625rem',
          fontWeight: 600
        },
        subheader: {
          fontSize: '0.8125rem',
          color: textSecondary
        }
      }
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '20px',
          '&:last-child': { paddingBottom: '20px' }
        }
      }
    },
    MuiCardActions: {
      styleOverrides: {
        root: { padding: '12px 20px', gap: '8px' }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          color: theme.darkTextPrimary,
          paddingTop: '8px',
          paddingBottom: '8px',
          borderRadius: '8px',
          '&.Mui-selected': {
            color: theme.menuSelected,
            backgroundColor: theme.menuSelectedBack,
            fontWeight: 600,
            '&:hover': {
              backgroundColor: theme.menuSelectedBack
            },
            '& .MuiListItemIcon-root': {
              color: theme.menuSelected
            }
          },
          '&:hover': {
            backgroundColor: hover,
            color: textPrimary,
            '& .MuiListItemIcon-root': {
              color: textPrimary
            }
          },
          ...focusRing
        }
      }
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: theme.darkTextSecondary,
          minWidth: '34px'
        }
      }
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: 'inherit',
          fontSize: '0.8125rem'
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          minHeight: '38px' // Ensures height for the input box inside Autocomplete
        },
        input: {
          color: textPrimary,
          '&::placeholder': {
            color: textSecondary,
            opacity: 1,
            fontSize: '0.875rem'
          }
        }
      }
    },
    // NOTE: this key used to be declared TWICE in this object. The first declaration —
    // carrying the input background, small-size padding and start-adornment spacing —
    // was silently discarded by the second, so none of it ever applied. Merged into one.
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          // Inputs are RECESSED below the surface they sit on. They were `#fff`, which
          // punched bright white holes into every dark form in the panel.
          backgroundColor: inset,
          color: textPrimary,
          borderRadius: radius,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: divider,
            transition: 'border-color .15s ease'
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.colors?.textDisabled
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: accentText,
            borderWidth: '2px'
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.colors?.stateError
          },
          '&.Mui-disabled': {
            backgroundColor: 'transparent',
            '& .MuiOutlinedInput-notchedOutline': { borderColor: divider }
          },
          '&.MuiInputBase-multiline': {
            padding: '10px 14px'
          }
        },
        input: {
          fontWeight: 400,
          fontSize: '0.875rem',
          color: textPrimary,
          padding: '10px 14px',
          borderRadius: radius,
          '&.MuiInputBase-inputSizeSmall': {
            padding: '8px 12px',
            '&.MuiInputBase-inputAdornedStart': {
              paddingLeft: 0
            }
          },
          '&.Mui-disabled': {
            WebkitTextFillColor: textDisabled
          }
        },
        inputAdornedStart: {
          paddingLeft: 4
        },
        notchedOutline: {
          borderRadius: radius
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
          color: textSecondary,
          marginLeft: '2px',
          '&.Mui-error': { color: theme.colors?.stateError }
        }
      }
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            color: theme.colors?.grey300
          }
        },
        mark: {
          backgroundColor: theme.paper,
          width: '4px'
        },
        valueLabel: {
          color: theme?.colors?.primaryLight
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: divider,
          opacity: 1
        }
      }
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          color: accentText,
          background: accentSoft,
          fontWeight: 600
        }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: '0.75rem',
          '&.MuiChip-deletable .MuiChip-deleteIcon': {
            color: 'inherit'
          }
        },
        // Status chips are read at a glance across long tables, so they carry a tinted
        // ground rather than a saturated fill — a wall of solid green and red pulls the
        // eye away from the row content it's meant to annotate.
        filledSuccess: { backgroundColor: 'rgba(52, 211, 153, 0.16)', color: theme.colors?.stateSuccess },
        filledError: { backgroundColor: 'rgba(248, 113, 113, 0.16)', color: theme.colors?.stateError },
        filledWarning: { backgroundColor: 'rgba(251, 191, 36, 0.16)', color: theme.colors?.stateWarning },
        filledInfo: { backgroundColor: 'rgba(96, 165, 250, 0.16)', color: theme.colors?.stateInfo }
      }
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            padding: '2px 8px'
          }
        },
        paper: {
          backgroundColor: raised,
          borderRadius: radius,
          boxShadow: shadowMenu,
          border: `1px solid ${divider}`
        },
        option: {
          color: textPrimary,
          fontSize: '0.875rem',
          '&[aria-selected="true"]': {
            backgroundColor: `${accentSoft} !important`,
            color: accentText
          },
          '&.Mui-focused': {
            backgroundColor: `${hover} !important`
          }
        }
      }
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          // Every Select was painted with the primary-action purple, so dropdowns read
          // as buttons and competed with the real primary action on the page. A select
          // is an input; it now looks like one.
          color: textPrimary,
          '& .MuiSelect-icon': {
            color: textSecondary
          }
        }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          color: textPrimary,
          backgroundColor: raised,
          border: `1px solid ${divider}`,
          fontSize: '0.75rem',
          boxShadow: shadowMenu
        },
        arrow: {
          color: raised
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: raised,
          backgroundImage: 'none',
          border: `1px solid ${divider}`,
          boxShadow: shadowMenu
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: textPrimary,
          backgroundColor: 'transparent',
          fontSize: '0.875rem',
          '&:hover': {
            backgroundColor: hover
          },
          '&.Mui-selected': {
            backgroundColor: accentSoft,
            color: accentText,
            '&:hover': {
              backgroundColor: accentSoft
            }
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: divider,
          color: textPrimary,
          fontSize: '0.875rem'
        },
        head: {
          // Header cells were once hard-coded to 16px while the rows they labelled sat at
          // 11.2px, so the labels shouted louder than the data. Quietening them overshot
          // the other way: at 11.5px in secondary ink they read as another, dimmer row
          // rather than as the thing naming the columns.
          //
          // 13px / 700 / primary ink is the middle. It is still SMALLER than the 14px body
          // text, so the data stays the loudest thing on the page — the header separates by
          // weight and case, not by size. Compared against three alternatives rendered side
          // by side; a heavier rule under the row was also tried and dropped, since the
          // contrast alone is enough and the extra line just added furniture.
          backgroundColor: raised,
          color: textPrimary,
          fontSize: '0.8125rem',
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap'
        }
      }
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover': {
            backgroundColor: hover
          }
        }
      }
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: textSecondary,
          borderTop: `1px solid ${divider}`
        },
        selectLabel: { fontSize: '0.8125rem', margin: 0 },
        displayedRows: { fontSize: '0.8125rem', margin: 0, fontVariantNumeric: 'tabular-nums' }
      }
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: accentText, height: '2px' }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'capitalize',
          fontSize: '0.875rem',
          fontWeight: 500,
          color: textSecondary,
          minHeight: '44px',
          '&.Mui-selected': { color: accentText },
          ...focusRing
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: { color: textSecondary, '&.Mui-checked': { color: accent }, ...focusRing }
      }
    },
    MuiRadio: {
      styleOverrides: {
        root: { color: textSecondary, '&.Mui-checked': { color: accent }, ...focusRing }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: { '&.Mui-checked': { color: accent }, ...focusRing }
      }
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: accentText,
          textDecorationColor: 'rgba(183, 156, 240, 0.4)',
          textUnderlineOffset: '2px',
          ...focusRing
        }
      }
    },
    MuiDrawer: {
      // Slide-overs (the chat panel) are content surfaces. The nav rail is chrome and
      // sets that itself in Sidebar/index.js — don't default every drawer to the rail
      // colour or the chat panel inherits it.
      styleOverrides: {
        paper: {
          backgroundColor: surface,
          backgroundImage: 'none',
          borderColor: divider
        }
      }
    }
  };
}
